import { atLength, CLOSE, CUBIC, LINE, MOVE, QUAD, subpathsOfCommands, type Paint, type PathCommands, type Ruler, type Subpath, type TextNode, type TextStyle } from '@flobi/artwork-core';
import type { ShapedText } from '@flobi/artwork-core/render';

/**
 * Type turned into outlines: the letters a text layer draws, as paths you can edit point by
 * point (Figma's Outline, Illustrator's Create Outlines).
 *
 * The letters are placed where the shaper put them — kerning, ligatures, line breaks,
 * alignment, Arabic joining, the box's vertical alignment — and each glyph's outline is read
 * from the very face file it was drawn with (`GlyphFace`). Each letter is a shape of its own
 * wearing its own run's fills, and the letters between two spaces (or a line's ends) are a
 * word, so a headline comes out as words that move as one and letters that can still be
 * picked one by one (Tarek, 2 Nov). Underlines and strikethroughs come out as bars of their own.
 */

/** A face's outlines, in font units with y up, as the font file has them. */
export interface GlyphFace {
  readonly unitsPerEm: number;
  readonly glyphCount: number;
  readonly weight: number;
  readonly italic: boolean;
  hasCharacter(codePoint: number): boolean;
  outline(glyph: number): PathCommands;
  /** Where the underline and the strike sit (y up, from the baseline) and how thick they are. */
  readonly underline: { readonly position: number; readonly thickness: number };
  readonly strike: { readonly position: number; readonly thickness: number };
}

/** The face a character in this style was drawn with: its family first, then the fallbacks. */
export type FaceFor = (style: TextStyle, codePoint: number, glyph: number, fallbacks: readonly string[]) => GlyphFace | null;

/** One letter's outline (a glyph: a ligature is one), or an underline or strike. */
export interface OutlinePiece {
  /** The characters it was made from. */
  readonly text: string;
  readonly fills: Paint[];
  readonly subpaths: Subpath[];
}

export interface OutlineWord {
  readonly text: string;
  readonly letters: OutlinePiece[];
}

export interface Outlines {
  readonly words: OutlineWord[];
  /** Underlines and strikethroughs, which run under several words at once. */
  readonly bars: OutlinePiece[];
  /** Letters left out because their face never loaded. */
  readonly missing: number;
}

/** How far a fake italic leans: Skia's own skew. */
const FAKE_ITALIC = 0.25;

export function outlineText(node: TextNode, shaped: ShapedText, faceFor: FaceFor, along?: Ruler | null): Outlines {
  const base = node.style;
  // The style each shaped character wears, by the run it came from.
  const starts = (node.runs ?? []).map((run, index) => ({ at: shaped.runStarts[index] ?? 0, style: { ...base, ...run.style } as TextStyle }));
  const styleAt = (index: number): TextStyle => {
    let found = starts[0]?.style ?? base;
    for (const start of starts) {
      if (start.at > index) break;
      found = start.style;
    }
    return found;
  };

  // The characters a glyph stands for run from its own to where the next glyph's begin, in
  // the text's order: right to left the glyphs come in the order they are seen, not read.
  const glyphStarts = [...new Set(shaped.lines.flatMap((line) => line.runs.flatMap((run) => run.characters.slice(0, -1))))].sort((a, b) => a - b);
  const endOf = (character: number): number => glyphStarts.find((at) => at > character) ?? shaped.text.length;
  const fillsOf = (style: TextStyle): Paint[] => (style.fills ?? []).map((fill) => structuredClone(fill));
  const shapeOf = (cmds: PathCommands): Subpath[] => subpathsOfCommands(cmds).filter((sub) => sub.closed);
  const words: OutlineWord[] = [];
  const bars: OutlinePiece[] = [];
  let word: { from: number; to: number; letters: OutlinePiece[] } | null = null;
  const endWord = () => {
    // Named by the characters it covers, in the text's order whichever way it reads.
    if (word && word.letters.length) words.push({ text: shaped.text.slice(word.from, word.to).trim(), letters: word.letters });
    word = null;
  };
  const start = node.onPath?.start ?? 0;
  const span = node.onPath?.span;
  let missing = 0;

  for (const line of shaped.lines) {
    // A word never runs from one line onto the next.
    endWord();
    for (const run of line.runs) {
      // An underline or strike runs on under consecutive glyphs of one look, as one bar.
      let bar: { kind: 'underline' | 'strikethrough'; from: number; to: number; y: number; face: GlyphFace; fills: string } | null = null;
      const endBar = () => {
        if (!bar) return;
        const scale = run.size / bar.face.unitsPerEm;
        const metric = bar.kind === 'underline' ? bar.face.underline : bar.face.strike;
        const thickness = Math.max(metric.thickness * scale, run.size / 20);
        const top = bar.y - metric.position * scale - thickness / 2;
        const cmds: PathCommands = [];
        place(cmds, [MOVE, bar.from, top, LINE, bar.to, top, LINE, bar.to, top + thickness, LINE, bar.from, top + thickness, CLOSE], bar.from, bar.to, line.baseline);
        bars.push({ text: bar.kind === 'underline' ? 'Underline' : 'Strikethrough', fills: JSON.parse(bar.fills) as Paint[], subpaths: shapeOf(cmds) });
        bar = null;
      };

      for (let i = 0; i < run.glyphs.length; i++) {
        const glyph = run.glyphs[i]!;
        const character = run.characters[i]!;
        const style = styleAt(character);
        const codePoint = shaped.text.codePointAt(character) ?? 32;
        const space = /\s/u.test(String.fromCodePoint(codePoint));
        const end = endOf(character);
        // A character with no glyph (a line break) is not part of the letter before it.
        const letter = shaped.text.slice(character, end).trim() || shaped.text.slice(character, end);
        if (space) endWord();
        const face = faceFor(style, codePoint, glyph, shaped.fallbacks);
        if (!face) {
          if (!space) missing++;
          endBar();
          continue;
        }
        const x = run.x[i]!;
        const y = run.y[i]!;
        const next = run.x[i + 1] ?? x;
        if (along && span !== undefined && Math.max(x, next) > span) break;
        const scale = run.size / face.unitsPerEm;
        const lean = run.fakeItalic ? FAKE_ITALIC : 0;
        const outline = face.outline(glyph);
        const cmds: PathCommands = [];
        for (let c = 0; c < outline.length; ) {
          const verb = outline[c++]!;
          cmds.push(verb);
          const points = verb === MOVE || verb === LINE ? 1 : verb === QUAD ? 2 : verb === CUBIC ? 3 : 0;
          for (let p = 0; p < points; p++) {
            const gx = outline[c++]!;
            const gy = outline[c++]!;
            cmds.push(x + (gx + lean * gy) * scale, y - gy * scale);
          }
        }
        const placed: PathCommands = [];
        place(placed, cmds, x, next, line.baseline);
        const subpaths = shapeOf(placed);
        if (!space && subpaths.length) {
          word ??= { from: character, to: end, letters: [] };
          word.from = Math.min(word.from, character);
          word.to = Math.max(word.to, end);
          word.letters.push({ text: letter, fills: fillsOf(style), subpaths });
        }

        const decoration = style.decoration === 'underline' || style.decoration === 'strikethrough' ? style.decoration : null;
        const fills = JSON.stringify(style.fills ?? []);
        if (bar && (bar.kind !== decoration || bar.fills !== fills)) endBar();
        if (decoration) {
          if (bar) bar.to = next;
          else bar = { kind: decoration, from: Math.min(x, next), to: Math.max(x, next), y, face, fills };
        }
      }
      endBar();
    }
  }

  /** Lays a glyph's commands down: as they are, or carried onto the curve. */
  function place(into: PathCommands, cmds: PathCommands, from: number, to: number, baseline: number): void {
    if (!along) {
      into.push(...cmds);
      return;
    }
    // The way the painter sets type on a path: the glyph's middle onto the point that far
    // along, its baseline along the tangent.
    const middle = (from + to) / 2;
    const spot = atLength(along, start + middle);
    if (!spot) return;
    const turn = (spot.angle * Math.PI) / 180;
    const cos = Math.cos(turn);
    const sin = Math.sin(turn);
    for (let c = 0; c < cmds.length; ) {
      const verb = cmds[c++]!;
      into.push(verb);
      const points = verb === MOVE || verb === LINE ? 1 : verb === QUAD ? 2 : verb === CUBIC ? 3 : 0;
      for (let p = 0; p < points; p++) {
        const dx = cmds[c++]! - middle;
        const dy = cmds[c++]! - baseline;
        into.push(spot.x + dx * cos - dy * sin, spot.y + dx * sin + dy * cos);
      }
    }
  }

  endWord();
  return { words, bars: bars.filter((bar) => bar.subpaths.length > 0), missing };
}
